import { getApiGen } from "$root/api/factory";
import { repositoryHelperFactory } from "$root/api/repository";
import { axiosExtract } from "$root/third-party-integrations/axios";
import {
	type Currencies,
	type CustomAttributeValue,
	type EditorSaveEditOrReviewRequest,
	type EditorSaveEditOrReviewRequestEditPolicyEnum,
	type EditorSaveEditOrReviewRequestPortfolioSavingModeEnum,
	type EditorSaveEditOrReviewResponse,
	type EditorSaveNewResponse,
	EntityEditorControllerApiFactory,
	type InvestmentBenchmark,
	type InvestmentReference,
	type ReviewTicker,
} from "@eu.mdotm.api/sphere";

/**
 * Page-agnostic gateway for the Entity Editor save endpoints, lifted out of
 * `pages/ManualCreation/handler.ts` so functional areas (e.g. `portfolio-lab`) can create or
 * commit a composition without depending on a page module. The previous `manualCreationSaveFactory`,
 * `manualCreationDraftFactory` and `manualSaveEditFactory` factories all live here now as repository
 * actions, grouped by intent:
 *
 * - `create.*`  — brand-new entities (`saveEditorNewEntity` / `…AsDraft`)
 * - `draft.*`   — operations on an existing `INVESTMENT_DRAFT` (commit it, or keep editing it)
 * - `edit.*`    — edits to an existing real entity (`saveEditorEditEntity`)
 *
 * Form parameters are typed against the generated editor types directly (not the page-local
 * `SubmitManualCreationPayload`) to keep this module page-agnostic.
 */

/** Subset of the manual-creation form actually consumed by the investment save endpoints. */
type InvestmentSaveForm = {
	name?: string;
	baseCurrency?: Currencies;
	primaryBenchmark?: InvestmentBenchmark;
	investmentReference?: InvestmentReference;
	portfolioSavingMode?: EditorSaveEditOrReviewRequestPortfolioSavingModeEnum;
	fixedWeights: boolean;
	liveTrading?: boolean;
};

/**
 * What a new composition carries next to it: in weights no AUM, in quantities always one. `aum` is what
 * the backend validates, sizes the composition against and stores as the AUM attribute. A draft's mode
 * and AUM are fixed when the draft is created, so the `draft.*` saves take only {@link DraftAttributesSave}.
 */
type QuantitySave =
	| { quantityMode?: false; aum?: never; customAttributes?: Array<CustomAttributeValue> }
	| { quantityMode: true; aum: number; customAttributes?: Array<CustomAttributeValue> };

/**
 * A draft save's custom attributes. Replace-all, as on the `edit.*` saves: it must be the draft's full
 * current set with the AUM entry upserted.
 */
type DraftAttributesSave = {
	customAttributes?: Array<CustomAttributeValue>;
};

/** What `create.investment` and `create.investmentDraft` take. */
export type NewInvestmentSave = { form: InvestmentSaveForm; composition: Array<ReviewTicker> } & QuantitySave;

/** What `draft.saveAsInvestment` and `draft.update` take. */
export type DraftInvestmentSave = {
	form: InvestmentSaveForm;
	composition: Array<ReviewTicker>;
	uuid: string;
} & DraftAttributesSave;

type TargetInvestmentSaveForm = {
	name: string;
	baseCurrency: Currencies;
	fixedWeights: boolean;
	type: "TARGET_INVESTMENT" | "BENCHMARK";
};

const { newAction } = repositoryHelperFactory("instrumentsEditor");

const entityEditorApi = () => getApiGen(EntityEditorControllerApiFactory);

export const instrumentsEditorRepository = {
	create: {
		investment: newAction((params: NewInvestmentSave): Promise<EditorSaveNewResponse> => {
			const { form, composition, quantityMode, aum, customAttributes } = params;
			return axiosExtract(
				entityEditorApi().saveEditorNewEntity("INVESTMENT", {
					quantityMode,
					aum,
					customAttributes,
					name: form.name,
					investmentBenchmark: form.primaryBenchmark,
					currency: form.baseCurrency,
					tickerComposition: composition,
					investmentReference: form.investmentReference,
					portfolioSavingMode: form.portfolioSavingMode,
					fixedWeights: form.fixedWeights,
					liveTrading: form.liveTrading,
				}),
			);
		}),
		investmentDraft: newAction((params: NewInvestmentSave): Promise<EditorSaveNewResponse> => {
			const { form, composition, quantityMode, aum, customAttributes } = params;
			return axiosExtract(
				entityEditorApi().saveEditorNewEntityAsDraft("INVESTMENT", {
					quantityMode,
					aum,
					customAttributes,
					name: form.name,
					investmentBenchmark: form.primaryBenchmark,
					currency: form.baseCurrency,
					tickerComposition: composition,
					investmentReference: form.investmentReference,
					portfolioSavingMode: form.portfolioSavingMode,
					fixedWeights: form.fixedWeights,
					liveTrading: form.liveTrading,
				}),
			);
		}),
		benchmark: newAction(
			(params: { form: { name: string }; composition: Array<ReviewTicker> }): Promise<EditorSaveNewResponse> => {
				const { form, composition } = params;
				return axiosExtract(
					entityEditorApi().saveEditorNewEntity("BENCHMARK", {
						name: form.name,
						tickerComposition: composition,
					}),
				);
			},
		),
		targetInvestment: newAction(
			(params: {
				form: TargetInvestmentSaveForm;
				composition: Array<ReviewTicker>;
			}): Promise<EditorSaveNewResponse> => {
				const { form, composition } = params;
				return axiosExtract(
					entityEditorApi().saveEditorNewEntity("TARGET_INVESTMENT", {
						name: form.name,
						currency: form.baseCurrency,
						tickerComposition: composition,
						fixedWeights: form.fixedWeights,
						benchmark: form.type === "BENCHMARK",
					}),
				);
			},
		),
		universe: newAction(
			(params: {
				form: { name: string; portfolioSavingMode?: EditorSaveEditOrReviewRequestPortfolioSavingModeEnum };
				composition: Array<ReviewTicker>;
			}): Promise<EditorSaveNewResponse> => {
				const { form, composition } = params;
				return axiosExtract(
					entityEditorApi().saveEditorNewEntity("UNIVERSE", {
						name: form.name,
						tickerComposition: composition,
						portfolioSavingMode: form.portfolioSavingMode,
					}),
				);
			},
		),
	},
	draft: {
		/** Commit an existing `INVESTMENT_DRAFT` to a real investment. */
		saveAsInvestment: newAction((params: DraftInvestmentSave): Promise<EditorSaveNewResponse> => {
			const { form, composition, uuid, customAttributes } = params;
			return axiosExtract(
				entityEditorApi().saveEditorEditEntity("INVESTMENT_DRAFT", {
					customAttributes,
					name: form.name,
					investmentBenchmark: form.primaryBenchmark,
					currency: form.baseCurrency,
					tickerComposition: composition,
					investmentReference: form.investmentReference,
					portfolioSavingMode: form.portfolioSavingMode,
					identifier: uuid,
					fixedWeights: form.fixedWeights,
					liveTrading: form.liveTrading,
				}),
			);
		}),
		/** Update an existing `INVESTMENT_DRAFT`, keeping it a draft. */
		update: newAction((params: DraftInvestmentSave): Promise<EditorSaveNewResponse> => {
			const { form, composition, uuid, customAttributes } = params;
			return axiosExtract(
				entityEditorApi().saveEditorEditEntityAsDraft("INVESTMENT_DRAFT", {
					customAttributes,
					name: form.name,
					investmentBenchmark: form.primaryBenchmark,
					currency: form.baseCurrency,
					tickerComposition: composition,
					investmentReference: form.investmentReference,
					portfolioSavingMode: form.portfolioSavingMode,
					identifier: uuid,
					fixedWeights: form.fixedWeights,
					liveTrading: form.liveTrading,
				}),
			);
		}),
	},
	edit: {
		investmentEnhancement: newAction(
			(
				uuid: string,
				params: {
					form: {
						portfolioSavingMode?: EditorSaveEditOrReviewRequestPortfolioSavingModeEnum;
						editPolicy?: EditorSaveEditOrReviewRequestEditPolicyEnum;
						/** Effective date for a "Save and accept in the past" (backdated) save. */
						editDate?: string;
					};
					composition: Array<ReviewTicker>;
					/**
					 * Replace-all: when set, the backend overwrites the investment's whole custom-attribute
					 * value set with this array — always send the full current set with any mutated entry,
					 * never the mutated entry alone. Omit to leave the attributes untouched.
					 */
					customAttributes?: Array<CustomAttributeValue>;
				},
			): Promise<EditorSaveEditOrReviewResponse> => {
				const { form, composition, customAttributes } = params;
				const request: EditorSaveEditOrReviewRequest = {
					identifier: uuid,
					tickerComposition: composition,
					portfolioSavingMode: form.portfolioSavingMode,
					editPolicy: form.editPolicy,
					editDate: form.editDate,
					customAttributes,
				};
				return axiosExtract(entityEditorApi().saveEditorEditEntity("INVESTMENT_ENHANCEMENT", request));
			},
		),
		investment: newAction(
			(
				uuid: string,
				params: {
					form: {
						portfolioSavingMode?: EditorSaveEditOrReviewRequestPortfolioSavingModeEnum;
						/** Effective date for a "Save in the past" (backdated) save. */
						editDate?: string;
					};
					composition: Array<ReviewTicker>;
					/**
					 * Replace-all: when set, the backend overwrites the investment's whole custom-attribute
					 * value set with this array — always send the full current set with any mutated entry,
					 * never the mutated entry alone. Omit to leave the attributes untouched.
					 */
					customAttributes?: Array<CustomAttributeValue>;
				},
			): Promise<EditorSaveEditOrReviewResponse> => {
				const { form, composition, customAttributes } = params;
				const request: EditorSaveEditOrReviewRequest = {
					identifier: uuid,
					tickerComposition: composition,
					portfolioSavingMode: form.portfolioSavingMode,
					editDate: form.editDate,
					customAttributes,
				};
				return axiosExtract(entityEditorApi().saveEditorEditEntity("INVESTMENT", request));
			},
		),
		benchmark: newAction(
			(uuid: string, params: { composition: Array<ReviewTicker> }): Promise<EditorSaveEditOrReviewResponse> => {
				const { composition } = params;
				return axiosExtract(
					entityEditorApi().saveEditorEditEntity("BENCHMARK", {
						identifier: uuid,
						tickerComposition: composition,
					}),
				);
			},
		),
		targetInvestment: newAction(
			(uuid: string, params: { composition: Array<ReviewTicker> }): Promise<EditorSaveEditOrReviewResponse> => {
				const { composition } = params;
				return axiosExtract(
					entityEditorApi().saveEditorEditEntity("TARGET_INVESTMENT", {
						identifier: uuid,
						tickerComposition: composition,
					}),
				);
			},
		),
		universe: newAction(
			(
				uuid: string,
				params: {
					form: { portfolioSavingMode: EditorSaveEditOrReviewRequestPortfolioSavingModeEnum };
					composition: Array<ReviewTicker>;
				},
			): Promise<EditorSaveEditOrReviewResponse> => {
				const { composition, form } = params;
				return axiosExtract(
					entityEditorApi().saveEditorEditEntity("UNIVERSE", {
						identifier: uuid,
						tickerComposition: composition,
						portfolioSavingMode: form.portfolioSavingMode,
					}),
				);
			},
		),
	},
};
